import { useState, useEffect } from 'react';
import { IoClose, IoShareSocialOutline, IoPeopleOutline, IoShieldCheckmarkOutline } from 'react-icons/io5';
import { MdAdminPanelSettings, MdVisibility, MdEdit, MdDelete, MdCheckCircle } from 'react-icons/md';
import { BsInfoCircle } from 'react-icons/bs';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ProjectFolderService from '@Helpers/URLHelpers/ProjectFolderService';
import UserPermissionViewService from '@Helpers/URLHelpers/AuthService/Views/UserPermissionViewService';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ErrorHandler from '@Root/ErrorHandler';
import './ProjectFilePermission.scss';

export default function ProjectFilePermission(props) {

    const [cursor, setCursor] = useState('auto');
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectAllUser, setSelectAllUser] = useState(true);
    const { currentProjectId } = useReduxProject();
    const activeUserId = JSON.parse(localStorage.getItem('activeUserId'));
    const projectFolderService = new ProjectFolderService();
    const userPermissionViewService = new UserPermissionViewService();
    const errorHandler = new ErrorHandler();
    const className = 'projectFilePermission';

    useEffect(() => {
        renderData();
    }, [])

    const renderData = async() => {
        try {
            setCursor('wait');
            setLoading(true);
            let parentType = !!props.childType ? props.childType : 'projectFolder';
            let parentId = props.folderId;
            let permissionBody = {
                projectId: currentProjectId,
                parentType: parentType,
                parentId: parentId
            }
            let response = await userPermissionViewService.getUserPermissionList(permissionBody);
            response = JSON.parse(response.body);
            response = response.map(user => ({...user, selected: user.allowView}));
            setUsersList(response);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setLoading(false);
        setCursor('auto');
    }

    const handleSubmit = async(e) => {
        e.preventDefault();
        setCursor('wait');
        try {
            let parentType = !!props.childType ? props.childType : 'projectFolder';
            let parentId = props.navigationParentId === 0 ? currentProjectId : props.navigationParentId;
            let userPermissionList = usersList.map(user => ({...user, folderId: props.folderId}));
            let bodyObj = {
                parentType: parentType,
                folderId: props.folderId,
                permissionArray: userPermissionList
            }
            let response = await projectFolderService.createProjectFolderPermission(bodyObj);
            if(response?.status < 400) {
                await props?.renderData(parentId);
                props.closePopup();
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleCheckAllUser = () => {
        let permissionObj = {
            selected: false,
            allowView: false,
            allowEdit: false,
            allowCreate: false,
            allowDelete: false,
            allowAdmin: false,
            externalViewer: false
        }
        if(!selectAllUser) {
            setUsersList(usersList.map(user => ({...user, selected: true})));
        } else {
            setUsersList(usersList.map(user => (user.userId === activeUserId ? {...user, selected: true} : {...user, ...permissionObj})));
        }
        setSelectAllUser(!selectAllUser);
    }

    const handleUserSelectCheckBox = (index) => {
        let user = usersList;
        user[index].selected = !user[index].selected;
        if(!user[index].selected) {
            let permissionObj = {
                selected: false,
                allowView: false,
                allowCreate: false,
                allowEdit: false,
                allowDelete: false,
                allowAdmin: false,
                externalViewer: false
            }
            user[index] = {...user[index], ...permissionObj}
        }
        setUsersList([...user]);
    }

    const handleReadPermissionsCheck = (index) => {
        let user = usersList;
        if(!user[index].allowAdmin) {
            user[index].allowView = !user[index].allowView;
        }
        setUsersList([...user]);
    }

    const handleEditPermissionsCheck = (index) => {
        let user = usersList;
        if(!user[index].allowAdmin) {
            user[index].allowEdit = !user[index].allowEdit;
        }
        setUsersList([...user]);
    }

    const handleDeletePermissionsCheck = (index) => {
        let user = usersList;
        if(!user[index].allowAdmin) {
            user[index].allowDelete = !user[index].allowDelete;
        }
        setUsersList([...user]);
    }

    const handleAdminPermissionsCheck = (index) => {
        let user = usersList;
        let adminValue = user[index].allowAdmin;
        user[index].allowAdmin = !adminValue;
        if(!adminValue) {
            user[index].allowView = true;
            user[index].allowEdit = true;
            user[index].allowDelete = true;
        }
        setUsersList([...user]);
    }

    const getInitials = (name) => {
        if (!name) return 'U';
        const names = name.split(' ');
        if (names.length >= 2) {
            return `${names[0][0]}${names[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getSelectedCount = () => {
        return usersList.filter(user => user.selected).length;
    };

    const getPermissionSummary = (user) => {
        const permissions = [];
        if (user.allowAdmin) return 'Full Access';
        if (user.allowView) permissions.push('View');
        if (user.allowEdit) permissions.push('Edit');
        if (user.allowDelete) permissions.push('Delete');
        return permissions.length > 0 ? permissions.join(', ') : 'No Access';
    };

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}
            className={className}>
            <Box className={className + '__container'} style={{cursor: cursor}}>
                {/* Header */}
                <Box className={className + '__header'}>
                    <Box className={className + '__header__left'}>
                        <Box className={className + '__header__icon'}>
                            <IoShareSocialOutline size={24} />
                        </Box>
                        <Box>
                            <Typography variant="h5" className={className + '__header__title'}>
                                Share & Manage Access
                            </Typography>
                            <Typography variant="body2" className={className + '__header__subtitle'}>
                                Control who can access this file
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={props.closePopup}
                        className={className + '__header__close'}
                        disabled={cursor === 'wait'}>
                        <IoClose />
                    </IconButton>
                </Box>

                <Divider />

                {/* Summary Bar */}
                <Box className={className + '__summary'}>
                    <Box className={className + '__summary__stat'}>
                        <IoPeopleOutline className={className + '__summary__stat__icon'} />
                        <Box>
                            <Typography variant="caption" className={className + '__summary__stat__label'}>
                                Total Users
                            </Typography>
                            <Typography variant="h6" className={className + '__summary__stat__value'}>
                                {usersList.length}
                            </Typography>
                        </Box>
                    </Box>
                    <Box className={className + '__summary__stat'}>
                        <MdCheckCircle className={className + '__summary__stat__icon'} />
                        <Box>
                            <Typography variant="caption" className={className + '__summary__stat__label'}>
                                Granted Access
                            </Typography>
                            <Typography variant="h6" className={className + '__summary__stat__value'}>
                                {getSelectedCount()}
                            </Typography>
                        </Box>
                    </Box>
                    <Box className={className + '__summary__stat'}>
                        <IoShieldCheckmarkOutline className={className + '__summary__stat__icon'} />
                        <Box>
                            <Typography variant="caption" className={className + '__summary__stat__label'}>
                                Admins
                            </Typography>
                            <Typography variant="h6" className={className + '__summary__stat__value'}>
                                {usersList.filter(u => u.allowAdmin).length}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider />

                {/* Permissions Table */}
                <Box className={className + '__content'}>
                    {/* Table Header */}
                    <Box className={className + '__table'}>
                        <Box className={className + '__table__header'}>
                            <Box className={className + '__table__header__cell ' + className + '__table__header__cell--user'}>
                                <Checkbox 
                                    checked={selectAllUser} 
                                    onChange={handleCheckAllUser}
                                    disabled={cursor === 'wait'}
                                    className={className + '__checkbox'}
                                />
                                <Typography variant="subtitle2">User</Typography>
                            </Box>
                            <Box className={className + '__table__header__cell ' + className + '__table__header__cell--permission'}>
                                <Tooltip 
                                    title="Allow users to view the file"
                                    arrow 
                                    placement="top"
                                    slots={{ transition: Zoom }}>
                                    <Box className={className + '__table__header__cell__content'}>
                                        <MdVisibility />
                                        <Typography variant="caption">View</Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                            <Box className={className + '__table__header__cell ' + className + '__table__header__cell--permission'}>
                                <Tooltip 
                                    title="Allow users to edit the file"
                                    arrow 
                                    placement="top"
                                    slots={{ transition: Zoom }}>
                                    <Box className={className + '__table__header__cell__content'}>
                                        <MdEdit />
                                        <Typography variant="caption">Edit</Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                            <Box className={className + '__table__header__cell ' + className + '__table__header__cell--permission'}>
                                <Tooltip 
                                    title="Allow users to delete the file"
                                    arrow 
                                    placement="top"
                                    slots={{ transition: Zoom }}>
                                    <Box className={className + '__table__header__cell__content'}>
                                        <MdDelete />
                                        <Typography variant="caption">Delete</Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                            <Box className={className + '__table__header__cell ' + className + '__table__header__cell--permission'}>
                                <Tooltip 
                                    title="Grant full administrative access"
                                    arrow 
                                    placement="top"
                                    slots={{ transition: Zoom }}>
                                    <Box className={className + '__table__header__cell__content'}>
                                        <MdAdminPanelSettings />
                                        <Typography variant="caption">Admin</Typography>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Table Body */}
                        <Box className={className + '__table__body'}>
                            {loading ? (
                                <Box className={className + '__table__loading'}>
                                    <CircularProgress size={40} />
                                    <Typography variant="body2">Loading users...</Typography>
                                </Box>
                            ) : (
                                usersList?.map((user, index) => (
                                    <Box 
                                        key={user.userId}
                                        className={`${className}__table__row ${user.userId === activeUserId ? className + '__table__row--current' : ''} ${!user.selected ? className + '__table__row--inactive' : ''}`}>
                                        <Box className={className + '__table__cell ' + className + '__table__cell--user'}>
                                            <Checkbox
                                                checked={user.selected}
                                                disabled={activeUserId === user.userId || cursor === 'wait'}
                                                onChange={() => handleUserSelectCheckBox(index)}
                                                className={className + '__checkbox'}
                                            />
                                            <Avatar className={className + '__table__cell__avatar'}>
                                                {getInitials(user.fullName)}
                                            </Avatar>
                                            <Box className={className + '__table__cell__info'}>
                                                <Typography variant="subtitle2" className={className + '__table__cell__name'}>
                                                    {user.fullName}
                                                    {user.userId === activeUserId && (
                                                        <Chip 
                                                            label="You" 
                                                            size="small"
                                                            className={className + '__table__cell__badge'}
                                                        />
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" className={className + '__table__cell__role'}>
                                                    {getPermissionSummary(user)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box className={className + '__table__cell ' + className + '__table__cell--permission'}>
                                            <Checkbox
                                                checked={user.allowView}
                                                disabled={user.allowAdmin || !user.selected || activeUserId === user.userId || cursor === 'wait'}
                                                onChange={() => handleReadPermissionsCheck(index)}
                                                className={className + '__checkbox'}
                                            />
                                        </Box>
                                        <Box className={className + '__table__cell ' + className + '__table__cell--permission'}>
                                            <Checkbox
                                                checked={user.allowEdit}
                                                disabled={user.allowAdmin || !user.selected || cursor === 'wait'}
                                                onChange={() => handleEditPermissionsCheck(index)}
                                                className={className + '__checkbox'}
                                            />
                                        </Box>
                                        <Box className={className + '__table__cell ' + className + '__table__cell--permission'}>
                                            <Checkbox
                                                checked={user.allowDelete}
                                                disabled={user.allowAdmin || !user.selected || cursor === 'wait'}
                                                onChange={() => handleDeletePermissionsCheck(index)}
                                                className={className + '__checkbox'}
                                            />
                                        </Box>
                                        <Box className={className + '__table__cell ' + className + '__table__cell--permission'}>
                                            <Checkbox
                                                checked={user.allowAdmin}
                                                disabled={!user.selected || cursor === 'wait'}
                                                onChange={() => handleAdminPermissionsCheck(index)}
                                                className={className + '__checkbox'}
                                            />
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Footer Actions */}
                <Box className={className + '__footer'}>
                    <Box className={className + '__footer__info'}>
                        <BsInfoCircle />
                        <Typography variant="caption">
                            {getSelectedCount()} of {usersList.length} users have access
                        </Typography>
                    </Box>
                    <Box className={className + '__footer__actions'}>
                        <Button
                            variant="outlined"
                            onClick={props.closePopup}
                            disabled={cursor === 'wait'}
                            className={className + '__footer__actions__cancel'}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={cursor === 'wait'}
                            className={className + '__footer__actions__submit'}
                            startIcon={cursor === 'wait' ? <CircularProgress size={16} /> : <MdCheckCircle />}>
                            {cursor === 'wait' ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}
